import React from "react";
import { Row, Col, ProgressBar, Badge, Alert, ToggleButtonGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { DashMedidasToggleButton } from "../DashMedidasButton/DashMedidasButton";
import {
  BsCpu,
  BsSpeedometer2,
  BsHeartPulse,
  BsShieldExclamation,
  BsExclamationTriangle,
  BsInfoCircle,
} from "react-icons/bs";
import { GRAIN_OPERATIONS, getScoreTooltipText } from "../../../../hooks/grainAnalyzer";
import DashMedidasSectionCard from "../DashMedidasSectionCard/DashMedidasSectionCard";
import "../DashMedidasSectionCard/DashMedidasSectionCard.css";
import "./GrainIntelligentAnalysis.css";

const SCORE_VARIANT = (score) => {
  if (score >= 90) return "success";
  if (score >= 70) return "warning";
  return "danger";
};

const RISK_VARIANT = (risk) => {
  const level = String(risk || "").toUpperCase();
  if (level === "ALTO") return "danger";
  if (level === "MÉDIO" || level === "MEDIO") return "warning";
  return "success";
};

const LEVEL_META = {
  NORMAL: { emoji: "🟢", className: "grain-health-level--normal", label: "Normal" },
  "ATENÇÃO": { emoji: "🟡", className: "grain-health-level--atencao", label: "Atenção" },
  ATENCAO: { emoji: "🟡", className: "grain-health-level--atencao", label: "Atenção" },
  CRÍTICO: { emoji: "🔴", className: "grain-health-level--critico", label: "Crítico" },
  CRITICO: { emoji: "🔴", className: "grain-health-level--critico", label: "Crítico" },
};

const HealthMetricRow = ({ label, metric }) => {
  if (!metric) return null;
  const levelKey = String(metric.level || "NORMAL").toUpperCase();
  const meta = LEVEL_META[levelKey] || LEVEL_META.NORMAL;
  const displayValue =
    metric.value == null || metric.value === ""
      ? "—"
      : `${Number(metric.value).toFixed(1)}${metric.unit || ""}`;

  return (
    <div className="grain-health-row" title={`${label}: ${meta.label}`}>
      <span className="grain-health-label">{label}</span>
      <span className="grain-health-row__values">
        <span className="grain-health-value">{displayValue}</span>
        <span className={`grain-health-level ${meta.className}`}>
          {meta.emoji} {meta.label}
        </span>
      </span>
    </div>
  );
};

const formatGrainHeaderRight = (grainLabel, curveValidity) => {
  if (!grainLabel && !curveValidity) return null;
  if (grainLabel && curveValidity && !String(grainLabel).includes("%")) {
    return `${grainLabel} ${curveValidity}`.trim();
  }
  return grainLabel || curveValidity;
};

const ScoreInfoTooltip = ({ operation }) => (
  <OverlayTrigger
    placement="top"
    trigger={["hover", "focus", "click"]}
    overlay={
      <Tooltip id={`grain-score-tooltip-${operation}`} className="grain-score-tooltip">
        {getScoreTooltipText(operation)}
      </Tooltip>
    }
  >
    <button
      type="button"
      className="grain-score-info-btn"
      aria-label="O que é o score geral?"
      onClick={(e) => e.stopPropagation()}
    >
      <BsInfoCircle aria-hidden />
    </button>
  </OverlayTrigger>
);

export const GrainAnalysisCardsGrid = ({ analysis }) => {
  const scoreVariant = SCORE_VARIANT(analysis.score);
  const { health } = analysis;

  return (
    <>
      <Row className="dash-medidas-grid">
        <Col xs={12} md={4}>
          <DashMedidasSectionCard
            compact
            icon={BsSpeedometer2}
            subtitle="Indicador"
            title="Score Geral"
            headerRight={<ScoreInfoTooltip operation={analysis.operation} />}
            bodyClassName="text-center"
          >
            <div className={`grain-score-value text-${scoreVariant}`}>
              {analysis.score}
            </div>
            <ProgressBar
              now={analysis.score}
              variant={scoreVariant}
              className="grain-score-progress"
              aria-label={`Score geral: ${analysis.score} de 100`}
            />
            <div className={`grain-score-status text-${scoreVariant}`}>
              {analysis.status}
            </div>
          </DashMedidasSectionCard>
        </Col>

        <Col xs={12} md={4}>
          <DashMedidasSectionCard
            compact
            icon={BsHeartPulse}
            subtitle="Médias"
            title="Saúde do Grão"
          >
            <HealthMetricRow label="Umidade média" metric={health?.humidity} />
            <HealthMetricRow label="Temperatura média" metric={health?.temperature} />
            <HealthMetricRow label="Densidade média" metric={health?.density} />
            <HealthMetricRow label="pH médio" metric={health?.ph} />
          </DashMedidasSectionCard>
        </Col>

        <Col xs={12} md={4}>
          <DashMedidasSectionCard
            compact
            icon={BsShieldExclamation}
            subtitle="Avaliação"
            title="Riscos"
          >
            <div className="grain-risk-item">
              <span className="grain-risk-label">Risco de fungos</span>
              <Badge bg={RISK_VARIANT(analysis.fungusRisk)} className="grain-risk-badge">
                {analysis.fungusRisk}
              </Badge>
            </div>
            <div className="grain-risk-item">
              <span className="grain-risk-label">Risco de deterioração</span>
              <Badge bg={RISK_VARIANT(analysis.deteriorationRisk)} className="grain-risk-badge">
                {analysis.deteriorationRisk}
              </Badge>
            </div>
            <div className="grain-risk-item">
              <span className="grain-risk-label">
                {analysis.estimatedStorageLabel || "Armazenamento estimado"}
              </span>
              <Badge bg="info" text="dark" className="grain-risk-badge">
                {analysis.estimatedStorage}
              </Badge>
            </div>
          </DashMedidasSectionCard>
        </Col>
      </Row>

      {analysis.recommendation && (
        <p className="grain-recommendation-inline mb-0">
          <strong>Recomendação:</strong> {analysis.recommendation}
        </p>
      )}

      {analysis.alerts?.length > 0 && (
        <Alert variant="warning" className="dash-alert-compact d-flex align-items-start gap-2 mb-0">
          <BsExclamationTriangle className="flex-shrink-0 mt-1" size={18} aria-hidden />
          <div>
            <strong className="d-block mb-1">Alertas do lote</strong>
            <ul className="mb-0 ps-3">
              {analysis.alerts.map((alert, index) => (
                <li key={`${alert}-${index}`}>{alert}</li>
              ))}
            </ul>
          </div>
        </Alert>
      )}
    </>
  );
};

const OperationSelector = ({ operation, onOperationChange, disabled }) => (
  <div className="grain-operation-selector">
    <span className="grain-operation-selector__label">Etapa da análise</span>
    <ToggleButtonGroup
      type="radio"
      name="grain-operation"
      value={operation}
      onChange={onOperationChange}
      className="dash-btn-toggle-group grain-operation-selector__group"
    >
      {GRAIN_OPERATIONS.map((op) => (
        <DashMedidasToggleButton
          key={op.id}
          id={`grain-op-${op.id}`}
          value={op.id}
          variant="outline-success"
          disabled={disabled}
        >
          {op.label}
        </DashMedidasToggleButton>
      ))}
    </ToggleButtonGroup>
  </div>
);

const GrainIntelligentAnalysis = ({
  analysis,
  error,
  grainLabel,
  curveValidity,
  operation,
  onOperationChange,
}) => {
  const headerRight = formatGrainHeaderRight(grainLabel, curveValidity);

  return (
    <section aria-labelledby="grain-analysis-heading">
      <span id="grain-analysis-heading" className="visually-hidden">
        Análise Inteligente do Lote
      </span>

      <DashMedidasSectionCard
        icon={BsCpu}
        subtitle="Análise"
        title="Inteligente do Lote"
        headerRight={headerRight}
        collapsible
        sectionId="dash-analise-inteligente"
      >
        <div className="dash-section-card__content-stack grain-analysis-body">
          <OperationSelector
            operation={operation}
            onOperationChange={onOperationChange}
            disabled={!grainLabel}
          />

          {error && (
            <Alert variant="secondary" className="mb-0">
              {error}
            </Alert>
          )}

          {!error && !analysis && (
            <div className="dash-section-empty">
              Selecione uma curva de grão e aguarde as medições para exibir a análise.
            </div>
          )}

          {!error && analysis && (
            <>
              {analysis.operationLabel && (
                <p className="grain-operation-active mb-0">
                  Análise para: <strong>{analysis.operationLabel}</strong>
                </p>
              )}
              <GrainAnalysisCardsGrid analysis={analysis} />
            </>
          )}
        </div>
      </DashMedidasSectionCard>
    </section>
  );
};

export default GrainIntelligentAnalysis;
